// =============================================================================
// eleicao.ts — Eleições com seed (issues #101 e #97): a ELEIÇÃO DO DIA e a
// ELEIÇÃO DA SEMANA. Uma infraestrutura só: um RNG determinista semeado pela
// string do período (America/Sao_Paulo) deixa a partida igual pra todo mundo —
// mesmos spawns, mesma urna, mesmo cassino — e o placar vira disputa justa.
// Aqui moram: o RNG, o cálculo de período/fechamento no fuso de Brasília,
// os modificadores do dia e os contratos da API /api/eleicao.
// =============================================================================
import { CHARACTER_LIST } from './characters';

// -----------------------------------------------------------------------------
// Modos com seed. SOLO por definição: placar comparável exige a mesma partida
// pra todo mundo (e dificuldade fixa em 'normal', decidida no servidor).
// -----------------------------------------------------------------------------
export type EleicaoMode = 'diaria' | 'semanal';

export function isEleicaoMode(v: unknown): v is EleicaoMode {
  return v === 'diaria' || v === 'semanal';
}

// -----------------------------------------------------------------------------
// RNG determinista: xmur3 (hash da seed em string) alimentando mulberry32.
// Rápido, sem dependências e igual em Node e navegador.
// -----------------------------------------------------------------------------
export function seededRng(seed: string): () => number {
  // xmur3: espreme a string num estado de 32 bits bem distribuído
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  let a = (h ^= h >>> 16) >>> 0;
  // mulberry32: o gerador em si
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// -----------------------------------------------------------------------------
// Modificadores da ELEIÇÃO DO DIA: 1 a 3 sorteados pela seed. A semanal roda
// a seed "pura", sem modificadores — a disputa é pelo melhor tempo de estrada.
// -----------------------------------------------------------------------------
export interface EleicaoModifierDef {
  id: string;
  nome: string;
  desc: string;
}

export const ELEICAO_MODIFIERS = {
  horario_eleitoral: {
    id: 'horario_eleitoral',
    nome: 'Horário Eleitoral Gratuito',
    desc: 'Inimigos 2x mais rápidos nos primeiros 5 minutos. Da propaganda ninguém escapa.',
  },
  dinheiro_na_cueca: {
    id: 'dinheiro_na_cueca',
    nome: 'Dinheiro na Cueca',
    desc: 'Maletas em dobro pelo mapa — mas a CPI da BET confisca com bem mais vontade.',
  },
  toffoli_apagou: {
    id: 'toffoli_apagou',
    nome: 'Toffoli Apagou',
    desc: 'Sigilo de 100 anos no minimapa: a apuração segue, mas às escuras.',
  },
  segundo_turno: {
    id: 'segundo_turno',
    nome: 'Segundo Turno',
    desc: 'Cada horário da agenda convoca DOIS bosses. A democracia agradece.',
  },
  voto_impresso: {
    id: 'voto_impresso',
    nome: 'Voto Impresso',
    desc: 'Personagem fixo do dia pra todo mundo: o papel não mente (dizem).',
  },
} satisfies Record<string, EleicaoModifierDef>;

export type EleicaoModifierId = keyof typeof ELEICAO_MODIFIERS;

export const ELEICAO_MODIFIER_LIST: EleicaoModifierDef[] = Object.values(ELEICAO_MODIFIERS);

// Lookup por id vindo da rede (string qualquer)
export const ELEICAO_MODIFIER_TABLE: Record<string, EleicaoModifierDef | undefined> = ELEICAO_MODIFIERS;

/** 1 a 3 modificadores do dia, sorteados deterministicamente pela seed. */
export function modifiersForSeed(seed: string): EleicaoModifierId[] {
  const rng = seededRng(`${seed}:mods`);
  const quantos = 1 + Math.floor(rng() * 3); // 1..3
  const bolsa = ELEICAO_MODIFIER_LIST.map((m) => m.id as EleicaoModifierId);
  const sorteados: EleicaoModifierId[] = [];
  while (sorteados.length < quantos && bolsa.length) {
    sorteados.push(bolsa.splice(Math.floor(rng() * bolsa.length), 1)[0]);
  }
  return sorteados;
}

/** Voto Impresso: o personagem fixo do período, também decidido pela seed. */
export function fixedCharForSeed(seed: string): string {
  const rng = seededRng(`${seed}:char`);
  return CHARACTER_LIST[Math.floor(rng() * CHARACTER_LIST.length)].id;
}

// -----------------------------------------------------------------------------
// Período e fechamento no fuso de Brasília (Intl API, sem libs novas).
// Diária: zera 00:00 America/Sao_Paulo. Semanal: segunda 00:00 a domingo 17h
// (das 17h de domingo até a virada, as urnas da semana estão FECHADAS).
// -----------------------------------------------------------------------------
const SP_TZ = 'America/Sao_Paulo';
const WEEKLY_CLOSE_HOUR = 17; // domingo, 17h de Brasília: fim da votação semanal

interface SpParts {
  y: number; m: number; d: number;
  hh: number; mm: number; ss: number;
  dow: number; // segunda = 0 ... domingo = 6
}

function spParts(now: Date): SpParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: SP_TZ, hour12: false, weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(now)) parts[p.type] = p.value;
  const dowMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return {
    y: Number(parts.year), m: Number(parts.month), d: Number(parts.day),
    hh: Number(parts.hour) % 24, mm: Number(parts.minute), ss: Number(parts.second),
    dow: dowMap[parts.weekday] ?? 0,
  };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Chave do dia em Brasília: '2026-07-05'. */
export function dailyKey(now: Date): string {
  const p = spParts(now);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}

/** Dia anterior de uma chave diária (aritmética de calendário pura). */
export function prevDailyKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12)); // meio-dia: longe de qualquer virada
  date.setUTCDate(date.getUTCDate() - 1);
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

/** Chave da semana ISO (segunda a domingo) do dia em Brasília: '2026-W27'. */
export function weeklyKey(now: Date): string {
  const p = spParts(now);
  const date = new Date(Date.UTC(p.y, p.m - 1, p.d));
  const dayNum = (date.getUTCDay() + 6) % 7;           // segunda = 0
  date.setUTCDate(date.getUTCDate() - dayNum + 3);     // quinta da mesma semana
  const ano = date.getUTCFullYear();
  const primeiraQuinta = new Date(Date.UTC(ano, 0, 4));
  const pqDayNum = (primeiraQuinta.getUTCDay() + 6) % 7;
  primeiraQuinta.setUTCDate(primeiraQuinta.getUTCDate() - pqDayNum + 3);
  const week = 1 + Math.round((date.getTime() - primeiraQuinta.getTime()) / (7 * 86400000));
  return `${ano}-W${pad2(week)}`;
}

/** Segundos até a meia-noite de Brasília (reset do placar diário). */
export function dailyCloseInSec(now: Date): number {
  const p = spParts(now);
  return 86400 - (p.hh * 3600 + p.mm * 60 + p.ss);
}

/**
 * Segundos até domingo 17h de Brasília (fim da votação semanal).
 * Valor <= 0 = urnas fechadas (domingo entre 17h e a virada pra segunda).
 */
export function weeklyCloseInSec(now: Date): number {
  const p = spParts(now);
  const segundosHoje = p.hh * 3600 + p.mm * 60 + p.ss;
  return (6 - p.dow) * 86400 + WEEKLY_CLOSE_HOUR * 3600 - segundosHoje;
}

/** Domingo antes das 17h: acende a tarja "ÚLTIMO DIA DE VOTAÇÃO". */
export function weeklyLastDay(now: Date): boolean {
  const p = spParts(now);
  return p.dow === 6 && weeklyCloseInSec(now) > 0;
}

/** Seed canônica do período (a string que alimenta o RNG da simulação). */
export function eleicaoSeed(mode: EleicaoMode, period: string): string {
  return `${mode}:${period}`;
}

// -----------------------------------------------------------------------------
// Streak "fidelidade partidária": dias consecutivos com tentativa registrada,
// contando de hoje pra trás (hoje ainda não jogado não quebra a sequência).
// -----------------------------------------------------------------------------
export function streakFromPeriods(periods: string[], today: string): number {
  const set = new Set(periods);
  let cursor = set.has(today) ? today : prevDailyKey(today);
  let streak = 0;
  while (set.has(cursor)) {
    streak++;
    cursor = prevDailyKey(cursor);
  }
  return streak;
}

// -----------------------------------------------------------------------------
// Contratos: o que viaja no game:start e na API /api/eleicao
// -----------------------------------------------------------------------------

// Dentro do GameStartPayload quando a partida é uma eleição com seed
export interface EleicaoStartInfo {
  mode: EleicaoMode;
  period: string;                    // '2026-07-05' | '2026-W27'
  modifiers: EleicaoModifierId[];
  scoring: boolean;                  // false = partida amistosa (não pontua)
}

// Estado de um período (diário ou semanal) na API
export interface EleicaoPeriodoInfo {
  mode: EleicaoMode;
  period: string;
  modifiers: EleicaoModifierId[];
  charId: string | null;             // Voto Impresso: personagem fixo do período
  aberta: boolean;                   // semanal fecha domingo 17h (Brasília)
  ultimoDia: boolean;                // tarja "ÚLTIMO DIA DE VOTAÇÃO"
  fechaEm: number;                   // segundos até o fim da votação do período
}

// GET /api/eleicao
export interface EleicaoStatusResponse {
  ok: boolean;
  configured: boolean;               // false = servidor sem Postgres: placar/streak indisponíveis
  logado: boolean;
  jaPontuou: boolean | null;         // diária: tentativa de hoje já usada (null = sem login/banco)
  streak: number | null;             // fidelidade partidária (null = sem login/banco)
  diaria: EleicaoPeriodoInfo;
  semanal: EleicaoPeriodoInfo;
}
